/*
 * quiz-engine.js -- "Find your routine" rules engine. Pure logic: no DOM, no network,
 * no storage, so the same file can power a later microsite and run under node for tests.
 *
 * Loaded in the browser as window.QuizEngine; under node via require().
 *
 * Input: theme/assets/quiz-catalogue.json (built by tools/build_quiz_catalogue_2026_09_25.py
 * from live products; flags such as hasParfum come from the INGREDIENT LIST, never from
 * tags or copy). Prices, images and availability are not in the catalogue.
 *
 * Copy rules (issues/enrichment-naming-rules.md sections 6 and 7): appearance and feel
 * wording only, no dashes, no exclamation marks. tools/quiz_engine_tests_2026_09_25.js scans
 * every string in this file for banned words.
 *
 * Deliberately NOT built in v1: the life stage question and any pregnancy rules (they need
 * legal review first). HOOK: to add later, append a question with key "lifeStage" to
 * QUESTIONS and read answers.lifeStage inside isEligible(). Do not persist it anywhere.
 */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory();
  else root.QuizEngine = factory();
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  var QUESTIONS = [
    {
      key: 'skinType', type: 'single',
      title: 'Where does your skin sit, most days?',
      ask: 'So we can match textures and richness to your skin.',
      options: [
        { v: 'dry', main: 'Dry', detail: 'Tight, sometimes flaky, rarely shiny' },
        { v: 'oily', main: 'Oily', detail: 'Shine comes back within a few hours, especially through the centre' },
        { v: 'combination', main: 'Combination', detail: 'Oily down the middle, drier or steadier around the edges' },
        { v: 'normal', main: 'Normal', detail: 'Comfortable most of the time, no strong pull in either direction' },
        { v: 'unknown', main: 'I am not sure', detail: 'That is fine. We will keep the choices gentle and broad.' }
      ]
    },
    {
      key: 'sensitivity', type: 'single',
      title: 'Is your skin easily upset?',
      ask: 'So we can keep the choices gentle where you need it.',
      options: [
        { v: 'yes', main: 'Yes', detail: 'It reacts to new products, fragrance, or a change in weather' },
        { v: 'sometimes', main: 'Sometimes', detail: 'The occasional flare, nothing constant' },
        { v: 'no', main: 'No', detail: 'It takes most things in its stride' }
      ]
    },
    {
      key: 'concerns', type: 'multi', max: 3,
      title: 'What would you most like to change?',
      ask: 'So we can focus your routine on what matters most to you.',
      sub: 'Choose up to three. We would rather build you something focused than something long.',
      options: [
        { v: 'dryness', main: 'Dryness and tightness' },
        { v: 'dullness', main: 'Dullness' },
        { v: 'dark-spots', main: 'Dark spots and uneven tone' },
        { v: 'fine-lines', main: 'The look of fine lines' },
        { v: 'pores', main: 'Shine and visible pores' },
        { v: 'blemish', main: 'Blemish-prone skin' },
        { v: 'flushed', main: 'Skin that looks flushed' },
        { v: 'texture', main: 'Rough or uneven texture' },
        { v: 'tired-eyes', main: 'Tired looking eyes' }
      ]
    },
    {
      key: 'depth', type: 'single',
      title: 'How much of a routine do you actually want?',
      ask: 'So the routine is the size you want it to be.',
      options: [
        { v: 'essentials', main: 'The essentials', detail: 'Cleanse, moisturise, protect' },
        { v: 'considered', main: 'A considered few', detail: 'With a serum doing the real work' },
        { v: 'full', main: 'The full routine', detail: 'I enjoy the layering' }
      ]
    },
    {
      key: 'spfHabit', type: 'single',
      title: 'Do you wear SPF most days?',
      ask: 'So we know how much to build sun protection into the routine.',
      options: [
        { v: 'daily', main: 'Every day, without thinking about it' },
        { v: 'sometimes', main: 'In summer, or when I remember' },
        { v: 'rarely', main: 'Not really' }
      ]
    },
    {
      key: 'fragrance', type: 'single',
      title: 'How do you feel about fragrance in your skincare?',
      ask: 'So we can look for products that suit your preference.',
      options: [
        { v: 'love', main: 'I love it', detail: 'The scent is part of why I enjoy this' },
        { v: 'neutral', main: 'I do not mind either way' },
        { v: 'avoid', main: 'I would rather avoid it', detail: 'We will look for products that do not list Parfum' }
      ]
    },
    {
      key: 'avoidances', type: 'multi', exclusive: 'none',
      title: 'Anything your skin has learned to avoid?',
      ask: 'So we can leave those out.',
      options: [
        { v: 'acid', main: 'Strong acids', detail: 'Glycolic, lactic and salicylic acid' },
        { v: 'vitaminC', main: 'Vitamin C' },
        { v: 'essentialOils', main: 'Essential oils' },
        { v: 'none', main: 'Nothing I know of' }
      ]
    }
  ];

  var SHADE_QUESTION = {
    key: 'shade', type: 'single', conditional: true,
    title: 'Your routine includes a tinted SPF. Which shade looks closest to your skin?',
    sub: 'This only picks a product shade. We do not keep it.'
  };
  // 2026-10-04 (quiz audit): a single-tint SPF has no shades to pick, so the question becomes a simple tint yes / no.
  var TINT_QUESTION = {
    key: 'shade', type: 'single', conditional: true,
    title: 'Your routine includes an SPF with a sheer tint. Is that right for you?',
    sub: 'It gives a light, skin-toned finish. We can pick one without a tint instead.'
  };

  var SLOT_LABEL = {
    remover: 'Makeup remover', cleanser: 'Cleanse', toner: 'Tone', serum: 'Serum', moisturiser: 'Moisturise',
    night: 'Night', oil: 'Face oil', eye: 'Eyes', eyemask: 'Eye masks', spf: 'Protect', weekly: 'Weekly'
  };
  var SLOT_ORDER = ['remover', 'cleanser', 'toner', 'serum', 'moisturiser', 'night', 'oil', 'eye', 'eyemask', 'spf', 'weekly'];

  // Core steps are the ones a routine is built around; everything else is offered as "add when you are ready".
  // This only labels items. It never changes which products are recommended.
  var CORE_SLOTS = ['cleanser', 'serum', 'moisturiser', 'spf'];
  // 2026-10-04 (quiz audit): optional steps are listed below the core steps on the results page, so each line also
  // says where the step goes in the routine.
  var SLOT_USAGE = {
    remover: 'Evening, before your cleanser, to remove makeup',
    cleanser: 'Morning and evening',
    toner: 'Morning and evening, after cleansing',
    serum: 'Morning or evening as directed on the pack, before moisturiser',
    moisturiser: 'Morning and evening',
    night: 'Evening, in place of your day moisturiser',
    oil: 'Evening, as the last layer',
    eye: 'Morning and evening, before moisturiser',
    eyemask: 'Two or three times a week, 15 minutes',
    spf: 'Morning, as the last step',
    weekly: 'Once or twice a week, as directed on the pack'
  };
  // When the routine has a night step, the day moisturiser is a morning step only.
  var DAY_MOISTURISER_WITH_NIGHT = 'Morning, before your SPF';

  var CONCERN_PHRASE = {
    dryness: 'skin that feels comfortable and hydrated',
    dullness: 'skin that looks brighter',
    'dark-spots': 'the look of an even tone',
    'fine-lines': 'the look of fine lines softened',
    pores: 'less midday shine and a more refined look',
    blemish: 'skin that looks clearer and more balanced',
    flushed: 'skin that looks calmer',
    texture: 'a smoother looking surface',
    'tired-eyes': 'eyes that look more rested'
  };
  var SKIN_PHRASE = { dry: 'dry', oily: 'oily', combination: 'combination', normal: 'normal' };

  var SKELETON = {
    essentials: ['cleanser', 'moisturiser', 'spf'],
    considered: ['cleanser', 'serum', 'moisturiser', 'spf'],
    full: ['cleanser', 'toner', 'serum', 'moisturiser', 'night', 'eye', 'spf']
  };
  // 2026-10-04 (owner approved): in the full routine the toner and eye steps are only required when they speak to a
  // chosen concern. Eye: required for these concerns, otherwise an optional extra. Toner: required when the toner we
  // would pick carries one of the chosen concerns, otherwise an optional extra (which, like every extra, must match a
  // chosen concern or is left out). This stops the full routine being padded with steps that target nothing asked for.
  var EYE_REQUIRED_FOR = ['tired-eyes', 'fine-lines'];
  var CONDITIONAL_FULL = ['toner', 'eye'];

  /* ---------------- helpers ---------------- */
  /*
   * 2026-10-03 rework. The first version scored on collection-derived data in which almost every
   * product was "any" skin type with one or no concerns, so ties were broken alphabetically and a
   * fragrance answer outweighed everything else: most answer sets got the same few products.
   * Now each product carries curated skin types, texture richness, primary and secondary concerns
   * and active families (tools/build_quiz_catalogue_2026_09_25.py CURATED), and the routine is
   * built as a whole: the serum step is picked first for the lead concern, later steps are pushed
   * towards concerns not yet covered, and active families are never doubled up or stacked past a
   * limit that depends on how easily the skin is upset.
   */

  function has(list, v) { return Array.isArray(list) && list.indexOf(v) > -1; }

  function isHeld(cat, p) { return has(cat.hold, p.handle); }

  // How many leave on active steps a routine may carry, by answer to "Is your skin easily upset?"
  var ACTIVE_CAP = { yes: 1, sometimes: 2, no: 3 };
  // Steps are chosen in this order so the treatment step gets first claim on the lead concern.
  var PICK_ORDER = ['serum', 'moisturiser', 'night', 'oil', 'eye', 'eyemask', 'weekly', 'toner', 'cleanser', 'remover', 'spf'];
  // Concern weights by the order the concern was chosen in (first choice counts most).
  var PRIMARY_W = [6, 4, 3], SECONDARY_W = [3, 2, 1.5];
  // Texture fit: index 0 light gel or water, 1 lotion, 2 rich cream, oil or milk.
  var RICH_FIT = { dry: [-2, 1, 2], oily: [2, 0, -3], combination: [1, 1, -2], normal: [0, 1, 0], unknown: [0, 1, -1] };
  // Added on top of RICH_FIT when "blemish" is one of their concerns (2026-10-04): light textures are
  // nudged up and rich creams, oils and milks pushed down, whatever the skin type. For dry skin the
  // two rows together favour a lotion over both a light gel and a rich cream.
  var BLEMISH_RICH = [1, 0, -3];

  // 2026-10-04 (owner approved): two acid exfoliants share the weekly step, so the catalogue gives each an acidTier.
  // "gentler" (Lactic Acid Overnight Concentrate) is the pick for dry, normal and not sure skin and for skin that is
  // sometimes upset; "standard" (Brightening Glycolic Acid Toner) for oily and combination skin that is not. This only
  // chooses between acids that are already allowed: easily upset skin, a strong acid avoidance, a retinol alternative
  // in the routine and the active cap still rule both out exactly as before.
  var GENTLER_ACID_SKIN = ['dry', 'normal', 'unknown'];
  function wantsGentlerAcid(a) {
    return a.sensitivity === 'sometimes' || !a.skinType || has(GENTLER_ACID_SKIN, a.skinType);
  }
  // 2026-10-04 (owner approved, follow up): dark spots with drier or sometimes upset skin. The gentler acid does not
  // carry dark spots, so when nothing else is left for the optional weekly extra the standard acid may come back, ONLY
  // as an optional extra (never a required or core step) and always with STRONGER_ACID_NOTE in the results notes.
  // Easily upset skin, a strong acid avoidance, a retinol alternative and the active cap still rule it out (those run
  // before this point), and its skin types still apply, so dry skin never gets it.
  var STRONGER_ACID_FOR = ['dark-spots'];
  function strongerAcidAllowed(p, a) {
    return p.acidTier === 'standard' && a.sensitivity !== 'yes' &&
      (a.concerns || []).some(function (c) { return has(STRONGER_ACID_FOR, c) && has(p.concerns, c); });
  }
  var STRONGER_ACID_NOTE = ' is a stronger exfoliating acid, so it is an optional extra. Start with once a week in the evening, ' +
    'patch test first, and skip it if your skin feels uncomfortable. Use SPF daily.';
  var STRONGER_ACID_USAGE = 'Evening, once a week to start';
  function isStrongerAcid(p, a) { return p.acidTier === 'standard' && wantsGentlerAcid(a); }
  function byAcidTier(pool, a, optional) {
    if (!pool.some(function (p) { return p.acidTier; })) return pool;
    if (wantsGentlerAcid(a)) {
      var gentler = pool.filter(function (p) { return p.acidTier !== 'standard'; });
      if (gentler.length || !optional) return gentler;
      return pool.filter(function (p) { return strongerAcidAllowed(p, a); });
    }
    // Oily or combination skin that is not easily upset: the standard acid when it is available, otherwise the gentler
    // one may stand in (it suits every skin type).
    var std = pool.filter(function (p) { return p.acidTier !== 'gentler'; });
    return std.some(function (p) { return p.acidTier === 'standard'; }) ? std : pool;
  }

  function activesOf(p) { return (p.actives || []).filter(function (x) { return x !== 'clay'; }); }
  function leaveOn(slot) { return slot !== 'cleanser'; }

  function isEligible(cat, p, a) {
    if (isHeld(cat, p)) return false;
    // Hard exclusions from the catalogue (e.g. the two-phase makeup remover is never offered to oily skin or
    // when blemish or pores is chosen).
    if (has(p.excludeSkinTypes, a.skinType)) return false;
    if ((a.concerns || []).some(function (c) { return has(p.excludeConcerns, c); })) return false;
    var f = p.flags || {};
    if (a.sensitivity === 'yes' && (f.hasAHA || f.hasBHA)) return false;
    // Skin that is easily upset: no named essential oils and no scrubs, whatever else was answered.
    if (a.sensitivity === 'yes' && (f.hasEssentialOils || has(p.actives, 'scrub'))) return false;
    // ...and nothing with alcohol high in the ingredient list (quiz audit 2026-10-04; it was only a score penalty).
    if (a.sensitivity === 'yes' && f.alcoholHigh) return false;
    var av = a.avoidances || [];
    if (has(av, 'acid') && (f.hasAHA || f.hasBHA)) return false;
    if (has(av, 'vitaminC') && f.hasVitaminC) return false;
    if (has(av, 'essentialOils') && f.hasEssentialOils) return false;
    if (a.shade === 'none' && ((p.shades && p.shades.length) || p.tinted)) return false;
    return true;
  }

  function skinFit(p, a) {
    // returns 'match' | 'any' | 'unknown' | 'mismatch'
    if (!a.skinType || a.skinType === 'unknown') return 'unknown';
    if (has(p.skinTypes, a.skinType)) return 'match';
    if (has(p.skinTypes, 'any')) return 'any';
    return 'mismatch';
  }

  function richFit(p, a, slot) {
    var row = RICH_FIT[a.skinType] || RICH_FIT.unknown;
    var r = p.richness;
    if (!(r >= 1 && r <= 3)) return 0;
    var v = row[r - 1];
    if (slot !== 'spf' && has(a.concerns, 'blemish')) v += BLEMISH_RICH[r - 1];
    return v;
  }

  function primaryOf(p) { return p.primary || []; }

  function score(p, a, slot, st) {
    var s = 0, fit = skinFit(p, a), f = p.flags || {};
    var covered = (st && st.covered) || {};
    // A product made for a narrower set of skin types is the more targeted match.
    if (fit === 'match') s += 4 + (4 - Math.min(4, (p.skinTypes || []).length)) * 0.5;
    else if (fit === 'any') s += 2;
    else if (fit === 'unknown') s += ((p.skinTypes || []).length >= 4 ? 1 : 0) + (p.gentle ? 2 : 0);
    s += richFit(p, a, slot);
    var cs = a.concerns || [];
    var leadHit = false;
    for (var i = 0; i < cs.length && i < 3; i++) {
      if (has(primaryOf(p), cs[i])) leadHit = true;
      var w = has(primaryOf(p), cs[i]) ? PRIMARY_W[i] : (has(p.concerns, cs[i]) ? SECONDARY_W[i] : 0);
      if (covered[cs[i]]) w = w / 2;
      s += w;
    }
    var nAct = leaveOn(slot) ? activesOf(p).length : 0;
    // An active only earns its place when it is built for something they asked about.
    if (nAct && !leadHit) s -= 2 * nAct;
    if (nAct && (!a.skinType || a.skinType === 'unknown')) s -= nAct;
    // Easily upset skin: fragrance allergens declared as coming from natural essential oils are a strong penalty, not a
    // ban (owner, 2026-10-04): every SPF carries them, so a ban would leave no SPF. The least-scented option wins where
    // one exists, and the routine notes name the rest with a patch test suggestion. Rinse-off cleansers are exempt (brief
    // contact), and so is a product that targets the shopper's first-chosen concern (owner, 2026-10-04: keep e.g. the
    // blemish moisturiser for blemish answers); the note still names it for a patch test.
    if (a.sensitivity === 'yes') { if (p.gentle) s += 5; if (f.alcoholHigh) s -= 3; if (f.hasEoAllergens && leaveOn(slot) && !has(p.concerns, cs[0])) s -= 4; s -= nAct; }
    else if (a.sensitivity === 'sometimes') { if (p.gentle) s += 1; if (f.hasEssentialOils) s -= 2; if (f.alcoholHigh) s -= 1; if (has(p.actives, 'scrub')) s -= 2; }
    else if (!a.skinType || a.skinType === 'unknown') { if (p.gentle) s += 1; }
    if ((a.skinType === 'dry' || has(cs, 'dryness')) && f.alcoholHigh) s -= 2;
    if (slot === 'cleanser' && has(cs, 'dryness') && p.gentle) s += 2;
    if (a.fragrance === 'avoid' && !f.hasParfum) s += 2;
    if (a.fragrance === 'love' && f.hasParfum) s += 1;
    if (slot === 'spf') {
      if (a.spfHabit === 'rarely' && /stick/i.test(p.title)) s += 3;
      if (p.spf >= 50) s += 1;
      var top = cs[0];
      var tinted = p.shades && p.shades.length;
      // A shade matched product adds a question, so it wins only when tone is the lead concern.
      if (tinted) s += (top === 'dark-spots' || top === 'dullness') ? 4 : -3;
    }
    return s;
  }

  // Would adding p break the routine level rules (no doubled actives, no stacking past the cap)?
  function conflicts(p, a, slot, st) {
    if (!leaveOn(slot)) return false;
    var acts = activesOf(p);
    // A retinol alternative eye product may sit alongside a face retinol alternative (owner, 2026-10-04): it is a
    // separate area and formulated for it. The active cap and the acid / scrub rules below still apply.
    for (var i = 0; i < acts.length; i++) {
      if (st.families[acts[i]] && !(slot === 'eye' && acts[i] === 'retinoid-alt')) return true;
    }
    var cap = ACTIVE_CAP[a.sensitivity] || ACTIVE_CAP.sometimes;
    if (st.activeCount + acts.length > cap) return true;
    var exfoliant = has(acts, 'aha') || has(acts, 'bha');
    if (exfoliant && st.families['retinoid-alt']) return true;
    if (has(acts, 'retinoid-alt') && (st.families.aha || st.families.bha)) return true;
    if (a.sensitivity !== 'no') {
      if (has(acts, 'scrub') && st.families['retinoid-alt']) return true;
      if (has(acts, 'retinoid-alt') && st.families.scrub) return true;
    }
    return false;
  }

  function candidates(cat, a, slot, st) {
    return cat.products.filter(function (p) {
      return p.slot === slot && st.used.indexOf(p.handle) === -1 && isEligible(cat, p, a) && !conflicts(p, a, slot, st);
    });
  }

  function best(cat, a, slot, st, optional) {
    var all = candidates(cat, a, slot, st);
    if (!all.length) return null;
    var strict = all.filter(function (p) { return skinFit(p, a) !== 'mismatch'; });
    if (optional) {
      // An optional extra has to earn its place: it must speak to something they asked about
      // (a face oil may also join a dry skin routine on texture alone).
      var cs = a.concerns || [];
      strict = strict.filter(function (p) {
        return slot === 'remover' || (slot === 'oil' && a.skinType === 'dry') || cs.some(function (c) { return has(p.concerns, c); });
      });
      if (!strict.length) return null;
    }
    var pool = strict.length ? strict : all;
    var relaxed = !strict.length;
    // Blemish answer: leave out rich creams, oils and milks (richness 3) whenever a lighter product
    // exists for the step. The eye step is exempt (a separate, small area); SPF is not graded on texture.
    if (has(a.concerns, 'blemish') && slot !== 'eye' && slot !== 'spf') {
      var lighter = pool.filter(function (p) { return p.richness !== 3; });
      if (lighter.length) pool = lighter;
    }
    if (a.fragrance === 'avoid') {
      var free = pool.filter(function (p) { return !p.flags.hasParfum; });
      if (free.length) pool = free;
    }
    pool = byAcidTier(pool, a, optional);
    if (!pool.length) return null;
    pool = pool.map(function (p) { return { p: p, s: score(p, a, slot, st) }; });
    // Ties: skin that can be upset (or not sure) takes the gentler, fewer-active product. Skin that is not easily
    // upset takes the leave-on product whose active is built for a concern they chose (2026-10-04, owner: an active is not
    // worse in itself; e.g. the Phyto-Retinol eye serum vs the Hexapeptide eye cream for fine lines).
    var chosen = (a.concerns || []).slice(0, 3);
    function targeted(p) {
      return leaveOn(slot) && activesOf(p).length && chosen.some(function (c) { return has(primaryOf(p), c); }) ? 1 : 0;
    }
    // 2026-10-04 (Mate Leaf fix): after those, a product from a line already in the routine (catalogue "line", e.g. the
    // Mate Leaf wash next to the Mate Leaf moisturiser) wins an exact tie, so equally good products are not always
    // settled alphabetically. It never changes a score or overrides any rule above.
    var lines = (st && st.lines) || {};
    function sameLine(p) { return p.line && lines[p.line] ? 1 : 0; }
    pool.sort(function (x, y) {
      return y.s - x.s || (a.sensitivity !== 'no' ? (y.p.gentle ? 1 : 0) - (x.p.gentle ? 1 : 0) : targeted(y.p) - targeted(x.p)) ||
        activesOf(x.p).length - activesOf(y.p).length || sameLine(y.p) - sameLine(x.p) || (x.p.handle < y.p.handle ? -1 : 1);
    });
    return { p: pool[0].p, relaxed: relaxed };
  }

  var TEXTURE_PHRASE = {
    dry: 'a richer texture for skin that runs dry',
    oily: 'a light texture for skin that gets shiny',
    combination: 'a light texture that will not feel heavy'
  };

  function reasons(p, a, slot) {
    var bits = [];
    var fit = skinFit(p, a);
    if (fit === 'match' && SKIN_PHRASE[a.skinType]) bits.push('made for skin that runs ' + SKIN_PHRASE[a.skinType]);
    if (p.acidTier === 'gentler' && wantsGentlerAcid(a)) bits.push('the gentler of our two weekly acid steps');
    (a.concerns || []).forEach(function (c) {
      if (has(p.concerns, c) && CONCERN_PHRASE[c]) bits.push('suited to ' + CONCERN_PHRASE[c]);
    });
    if (slot !== 'spf' && TEXTURE_PHRASE[a.skinType] && richFit(p, a) >= 2) bits.push(TEXTURE_PHRASE[a.skinType]);
    else if (slot !== 'spf' && has(a.concerns, 'blemish') && a.skinType !== 'dry' && p.richness === 1) bits.push(TEXTURE_PHRASE.combination);
    if (a.sensitivity === 'yes' && p.gentle && !(p.flags && p.flags.hasParfum)) bits.push('picked for skin that is easily upset');
    if (a.fragrance === 'avoid' && !p.flags.hasParfum) bits.push('does not list Parfum, as you asked');
    if (slot === 'spf' && a.spfHabit === 'rarely' && /stick/i.test(p.title)) bits.push('a stick is easy to keep using');
    if (slot === 'spf' && p.spf >= 50 && bits.length < 2) bits.push('SPF50 for everyday daylight');
    return bits;
  }

  function why(p, a, slot, relaxed) {
    var bits = reasons(p, a, slot);
    var line = bits.length ? 'Chosen because it is ' + bits.slice(0, 3).join(' and ') + '.' : 'The closest match in this step for your answers.';
    if (relaxed) line += ' We do not have a product in this step made specifically for your skin type, so this is the closest match.';
    return line;
  }

  function usageFor(p, slot) {
    if (p.usage) return p.usage;
    if (p.time === 'pm' && slot !== 'oil' && slot !== 'night') {
      return slot === 'serum' ? 'Evening, before moisturiser' : 'Evening, as directed on the pack';
    }
    if (p.time === 'am') return (slot === 'serum' || slot === 'eye') ? 'Morning, before moisturiser and SPF' : 'Morning, before your SPF';
    return SLOT_USAGE[slot] || '';
  }

  function extraSlot(a) {
    var cs = a.concerns || [];
    if (has(cs, 'tired-eyes')) return 'eye';
    if (has(cs, 'texture') || has(cs, 'dark-spots') || has(cs, 'dullness') || has(cs, 'pores') || has(cs, 'blemish')) return 'weekly';
    return null;
  }

  var DEPTH_PHRASE = { essentials: 'the essentials', considered: 'a considered few steps', full: 'the full routine' };

  // One plain sentence describing what they told us, in appearance and feel words only. No health or life stage data.
  function summaryFor(a) {
    var bits = [];
    if (a.skinType && SKIN_PHRASE[a.skinType]) bits.push('Skin that runs ' + SKIN_PHRASE[a.skinType]);
    else bits.push('Skin you are still getting to know');
    if (a.sensitivityAssumed) { /* not in the link, so not said back */ }
    else if (a.sensitivity === 'yes') bits.push('easily upset');
    else if (a.sensitivity === 'sometimes') bits.push('sometimes upset');
    var line = bits.join(', ') + '.';
    var cs = (a.concerns || []).map(function (c) { return CONCERN_PHRASE[c]; }).filter(Boolean);
    if (cs.length) line += ' You would like ' + (cs.length > 1 ? cs.slice(0, -1).join(', ') + ' and ' + cs[cs.length - 1] : cs[0]) + '.';
    if (a.depth && DEPTH_PHRASE[a.depth]) line += ' You asked for ' + DEPTH_PHRASE[a.depth] + '.';
    return line;
  }

  /* ---------------- routine ---------------- */

  function buildRoutine(cat, a) {
    var depth = a.depth || 'essentials';
    var cs0 = a.concerns || [];
    var plan = (SKELETON[depth] || SKELETON.essentials).map(function (s) {
      if (depth === 'full' && has(CONDITIONAL_FULL, s)) {
        // eye: required only for the concerns it is there for; toner: decided once the toner is picked (below)
        var eyeNeeded = s === 'eye' && cs0.some(function (c) { return has(EYE_REQUIRED_FOR, c); });
        return { slot: s, optional: false, conditional: !eyeNeeded };
      }
      return { slot: s, optional: false };
    });
    if (depth === 'considered') {
      var ex = extraSlot(a);
      if (ex) plan.push({ slot: ex, optional: true });
      // Hydrogel eye masks are an occasional extra alongside the eye step, never in place of it (2026-10-04).
      if (has(cs0, 'tired-eyes')) plan.push({ slot: 'eyemask', optional: true });
    }
    // 2026-10-04: tired looking eyes at the essentials size gets an optional eye step (the skeleton has no eye step).
    if (depth === 'essentials' && has(cs0, 'tired-eyes')) plan.push({ slot: 'eye', optional: true });
    if (depth === 'full') {
      plan.push({ slot: 'weekly', optional: true });
      // The two-phase makeup remover is a first cleanse ("Follow with your second cleanser"), so it is only ever an
      // optional extra alongside the cleanser step, never the cleanser itself (quiz audit 2026-10-04).
      plan.push({ slot: 'remover', optional: true });
      if (has(cs0, 'tired-eyes') || has(cs0, 'fine-lines')) plan.push({ slot: 'eyemask', optional: true });
      // No face oil step for a blemish answer: it is the richest step we make.
      if (!has(a.concerns || [], 'blemish') && (has(a.concerns || [], 'dryness') || a.skinType === 'dry')) plan.push({ slot: 'oil', optional: true });
    }
    plan.sort(function (x, y) { return PICK_ORDER.indexOf(x.slot) - PICK_ORDER.indexOf(y.slot); });
    var st = { used: [], families: {}, activeCount: 0, covered: {}, lines: {} };
    var items = [], missing = [];
    plan.forEach(function (step) {
      var pick;
      if (step.conditional) {
        // Keep the step as required when the product we would pick targets a chosen concern (the toner case;
        // the eye case only reaches here when no eye concern was chosen). Otherwise offer it as an optional extra.
        pick = step.slot === 'toner' ? best(cat, a, step.slot, st, false) : null;
        if (!(pick && cs0.some(function (c) { return has(pick.p.concerns, c); }))) {
          step.optional = true;
          pick = best(cat, a, step.slot, st, true);
        }
      } else {
        pick = best(cat, a, step.slot, st, step.optional);
      }
      if (!pick) { if (!step.optional) missing.push(step.slot); return; }
      var p = pick.p;
      var stronger = isStrongerAcid(p, a);
      // Safety net: the stronger acid is never a required step for these answers (byAcidTier only lets it into extras).
      if (stronger && !step.optional) { missing.push(step.slot); return; }
      st.used.push(p.handle);
      if (p.line) st.lines[p.line] = true;
      if (leaveOn(step.slot)) {
        activesOf(p).forEach(function (f) { st.families[f] = true; });
        st.activeCount += activesOf(p).length;
      }
      primaryOf(p).forEach(function (c) { st.covered[c] = true; });
      items.push({
        slot: step.slot, handle: p.handle, title: p.title, optional: step.optional,
        relaxed: pick.relaxed, hasParfum: p.flags.hasParfum, hasEoAllergens: !!p.flags.hasEoAllergens, shades: p.shades || [],
        actives: activesOf(p), time: p.time || 'any', tinted: !!p.tinted,
        why: why(p, a, step.slot, pick.relaxed),
        reasons: reasons(p, a, step.slot).slice(0, 3),
        usage: stronger ? STRONGER_ACID_USAGE : usageFor(p, step.slot),
        strongerAcid: stronger,
        core: !step.optional && has(CORE_SLOTS, step.slot)
      });
    });
    items.sort(function (x, y) { return SLOT_ORDER.indexOf(x.slot) - SLOT_ORDER.indexOf(y.slot); });
    if (items.some(function (i) { return i.slot === 'night'; })) {
      items.forEach(function (i) {
        if (i.slot === 'moisturiser' && i.usage === SLOT_USAGE.moisturiser) i.usage = DAY_MOISTURISER_WITH_NIGHT;
      });
    }
    // Safety net: a routine always has a core. If none of the core slots could be filled, the required steps become core.
    if (items.length && !items.some(function (i) { return i.core; })) {
      items.forEach(function (i) { if (!i.optional) i.core = true; });
      if (!items.some(function (i) { return i.core; })) items[0].core = true;
    }
    return { items: items, missing: missing, notes: notesFor(items, a, missing), summary: summaryFor(a) };
  }

  function notesFor(items, a, missing) {
    var notes = [];
    if (a.fragrance === 'avoid') {
      var withParfum = items.filter(function (i) { return i.hasParfum; });
      if (!items.length) { /* nothing to say */ }
      else if (!withParfum.length) notes.push('None of these list Parfum in their ingredients, as you asked.');
      else notes.push('You asked us to avoid fragrance. ' + withParfum.map(function (i) { return i.title; }).join(', ') +
        (withParfum.length > 1 ? ' list' : ' lists') + ' Parfum in ' + (withParfum.length > 1 ? 'their' : 'its') +
        ' ingredients. ' + (withParfum.length > 1 ? 'They are' : 'It is') + ' the only ' + (withParfum.length > 1 ? 'options' : 'option') +
        ' we make for that step that suit your other answers, so we kept ' +
        (withParfum.length > 1 ? 'them' : 'it') + '. Use Change an answer if you would rather see fewer options.');
    }
    if (missing.length) {
      notes.push('We left out ' + missing.map(function (s) { return SLOT_LABEL[s].toLowerCase(); }).join(' and ') +
        ' because nothing in that step fits every answer you gave.');
    }
    var av = a.avoidances || [];
    if (a.sensitivity === 'yes') {
      notes.push('Because your skin is easily upset, we left out strong acids, essential oils, scrubs and products high in alcohol, and kept to one active step at most.');
      var eoAll = items.filter(function (i) { return i.hasEoAllergens; });
      if (eoAll.length) {
        notes.push(eoAll.map(function (i) { return i.title; }).join(', ') + (eoAll.length > 1 ? ' contain' : ' contains') +
          ' fragrance allergens from natural essential oils (listed in the ingredients). We chose the gentlest option available for each step; patch test before using ' +
          (eoAll.length > 1 ? 'them' : 'it') + ' all over.');
      }
      var scented = items.filter(function (i) { return i.hasParfum && !i.hasEoAllergens; });
      if (scented.length && a.fragrance !== 'avoid') {
        notes.push(scented.map(function (i) { return i.title; }).join(', ') + (scented.length > 1 ? ' list' : ' lists') +
          ' Parfum (fragrance) in ' + (scented.length > 1 ? 'their' : 'its') + ' ingredients. Patch test any new product before using it all over.');
      }
    }
    else if (has(av, 'acid')) notes.push('We left out products with strong acids.');
    if (has(av, 'vitaminC')) notes.push('We left out products with vitamin C.');
    if (has(av, 'essentialOils') && a.sensitivity !== 'yes') notes.push('We left out products that list essential oils.');
    var hasRetAlt = items.some(function (i) { return has(i.actives, 'retinoid-alt'); });
    // Evening-only retinol alternatives get the evening note; the eye serum's pack says morning and evening.
    if (items.some(function (i) { return has(i.actives, 'retinoid-alt') && i.time === 'pm'; })) {
      notes.push('Your routine includes a plant based retinol alternative. Use it in the evening and keep your SPF going every morning.');
    }
    var weekly = items.filter(function (i) { return i.slot === 'weekly'; })[0];
    if (hasRetAlt && weekly) notes.push('On the evenings you use ' + weekly.title + ', leave out the retinol alternative.');
    // A daily acid toner plus a weekly acid step: never both on the same evening (quiz audit 2026-10-04).
    var isAcid = function (i) { return has(i.actives, 'aha') || has(i.actives, 'bha'); };
    var acidToner = items.filter(function (i) { return i.slot === 'toner' && isAcid(i); })[0];
    if (acidToner && weekly && isAcid(weekly)) {
      notes.push('On the evenings you use ' + weekly.title + ', skip ' + acidToner.title + ' so you only use one exfoliating acid that night.');
    }
    var strongerAcid = items.filter(function (i) { return i.strongerAcid; })[0];
    if (strongerAcid) notes.push(strongerAcid.title + STRONGER_ACID_NOTE);
    var nAct = items.filter(function (i) { return i.slot !== 'cleanser' && i.actives && i.actives.length; }).length;
    if (nAct > 1) notes.push('Introduce one new step at a time, a few days apart, so you can see how your skin takes to each.');
    return notes;
  }

  /* ---------------- flow ---------------- */

  function needsShade(cat, a) {
    var probe = {};
    for (var k in a) probe[k] = a[k];
    probe.shade = undefined;
    var r = buildRoutine(cat, probe);
    var spf = r.items.filter(function (i) { return i.slot === 'spf'; })[0];
    return !!(spf && ((spf.shades && spf.shades.length) || spf.tinted));
  }

  function probeSpf(cat, a) {
    var probe = {};
    for (var k in a) probe[k] = a[k];
    probe.shade = undefined;
    return buildRoutine(cat, probe).items.filter(function (i) { return i.slot === 'spf'; })[0];
  }

  function shadeQuestion(cat, a) {
    var spf = probeSpf(cat, a);
    return spf && !(spf.shades && spf.shades.length) && spf.tinted ? TINT_QUESTION : SHADE_QUESTION;
  }

  function shadeOptions(cat, a) {
    var probe = {};
    for (var k in a) probe[k] = a[k];
    probe.shade = undefined;
    var spf = buildRoutine(cat, probe).items.filter(function (i) { return i.slot === 'spf'; })[0];
    if (spf && !(spf.shades && spf.shades.length) && spf.tinted) {
      return [
        { v: 'tint', main: 'Yes, a light tint is fine', detail: 'Keep the tinted SPF' },
        { v: 'none', main: 'I would rather have no tint', detail: 'We will pick an SPF without a tint' }
      ];
    }
    var opts = ((spf && spf.shades) || []).map(function (s) { return { v: s, main: s }; });
    opts.push({ v: 'unsure', main: 'Show me all shades', detail: 'You can choose on the next screen' });
    opts.push({ v: 'none', main: 'I would rather have no tint', detail: 'We will pick an SPF without a shade' });
    return opts;
  }

  function flowLength(cat, a) { return QUESTIONS.length + (needsShade(cat, a) ? 1 : 0); }

  /* ---------------- results link ---------------- */

  var CODE = { skinType: 's', sensitivity: 'x', concerns: 'c', depth: 'd', spfHabit: 'p', fragrance: 'f', avoidances: 'a', shade: 'h' };

  function b64(str) {
    var s = typeof btoa === 'function' ? btoa(unescape(encodeURIComponent(str))) : Buffer.from(str, 'utf8').toString('base64');
    return s.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }
  function unb64(str) {
    var s = str.replace(/-/g, '+').replace(/_/g, '/');
    while (s.length % 4) s += '=';
    return typeof atob === 'function' ? decodeURIComponent(escape(atob(s))) : Buffer.from(s, 'base64').toString('utf8');
  }

  // 2026-10-04 owner decision: the sensitivity answer is never written into the results link (the link is readable
  // base64, it is shared and it goes into the routine email). decodeAnswers fills a neutral default instead.
  var LINK_OMIT = { sensitivity: 1 };
  function encodeAnswers(a) {
    var o = {};
    Object.keys(CODE).forEach(function (k) { if (!LINK_OMIT[k] && a[k] !== undefined && a[k] !== null && a[k] !== '') o[CODE[k]] = a[k]; });
    return b64(JSON.stringify(o));
  }

  function validValues(key) {
    var q = QUESTIONS.filter(function (x) { return x.key === key; })[0];
    return q ? q.options.map(function (o) { return o.v; }) : null;
  }

  function decodeAnswers(str, cat) {
    var o;
    try { o = JSON.parse(unb64(str)); } catch (e) { return null; }
    if (!o || typeof o !== 'object') return null;
    var a = {};
    Object.keys(CODE).forEach(function (k) {
      var v = o[CODE[k]];
      if (v === undefined) return;
      var ok = validValues(k);
      if (k === 'shade') {
        var shades = {};
        ((cat && cat.products) || []).forEach(function (p) { (p.shades || []).forEach(function (s) { shades[s] = 1; }); });
        if (typeof v === 'string' && (shades[v] || v === 'unsure' || v === 'none' || v === 'tint')) a.shade = v;
      } else if (Array.isArray(v)) {
        a[k] = v.filter(function (x) { return ok && has(ok, x); }).slice(0, k === 'concerns' ? 3 : 4);
      } else if (ok && has(ok, v)) a[k] = v;
    });
    // Links made since 2026-10-04 carry no sensitivity answer: assume the middle answer and keep it out of the summary.
    // (The quiz page swaps in the visitor's own saved answer when it has one.) Older links that include it still work.
    if (a.sensitivity === undefined) { a.sensitivity = 'sometimes'; a.sensitivityAssumed = true; }
    var required = ['skinType', 'sensitivity', 'concerns', 'depth', 'spfHabit', 'fragrance', 'avoidances'];
    for (var i = 0; i < required.length; i++) if (a[required[i]] === undefined || (Array.isArray(a[required[i]]) && !a[required[i]].length && required[i] !== 'concerns')) return null;
    return a;
  }

  return {
    QUESTIONS: QUESTIONS, SHADE_QUESTION: SHADE_QUESTION, SLOT_LABEL: SLOT_LABEL, SLOT_ORDER: SLOT_ORDER, SLOT_USAGE: SLOT_USAGE, CORE_SLOTS: CORE_SLOTS,
    isEligible: isEligible, buildRoutine: buildRoutine, needsShade: needsShade, shadeOptions: shadeOptions, shadeQuestion: shadeQuestion,
    flowLength: flowLength, encodeAnswers: encodeAnswers, decodeAnswers: decodeAnswers
  };
});
